import React, { forwardRef, useMemo, useCallback, useRef, useState } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import _ from 'lodash';
import { FieldTypes } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import { Form } from 'antd';
const ReversingLabSpectraMappingForm = forwardRef(function ReversingLabSpectraMappingForm({ disableProjectFields = false, ...props }, ref) {
  const [form] = Form.useForm();
  const paramAPIRef = useRef({});

  const [groups, setGroups] = useState(() => {
    if (props.record) {
      const { groupId, group } = props.record;
      return [{id:groupId,name:group}]
    }
    return [];
  });
  const [projects, setProjects] = useState(() => {
    if (props.record) {
      const { projectId, project } = props.record;
      return [{id:projectId,name:project}]
    }
    return [];
  });
  const [packages, setPackages] = useState(() => {
    if (props.record) {
      const { packageId, package: _package, groupProjectAndPackageId, groupProjectAndPackageName } = props.record;
      return [{id:packageId,name:_package, otherProperties: { groupProjectAndPackageId, groupProjectAndPackageName }}]
    }
    return [];
  });
  const [releases, setReleases] = useState(() => {
    if (props.record) {
      const { release, releaseId } = props.record;
      const releaseIdArray = _.isEmpty(releaseId) ? [] :releaseId.split(',');
            const releaseNameArray = _.isEmpty(release) ? [] :release.split(',');
            if (releaseIdArray.length === 0) {
              return [];
            }
            return releaseIdArray.map((id, index) => ({id, name: releaseNameArray[index]}))
    }
    return [];
  });

  const fetchFieldResponse = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
      login_id: props.loginConfigId,
      ...params,
    });

    const modiFiedpromise = promise.then((res) => {
      const { projects } = res;
      const type = params?.type ?? 'group';

      if(type === 'group'){
        setGroups(projects);
      }
      else if (type === 'project') {
        setProjects((prevProjects) => _.uniqBy([...prevProjects, ...projects], 'id'));
      }
      else if (type === 'package') {
        setPackages((prevPackages) => _.uniqBy([...prevPackages, ...projects], 'id'));
      }
      else if (type === 'release') {
        setReleases((prevReleases) => _.uniqBy([...prevReleases, ...projects], 'id'));
      }
      
      return projects;
    });

    return { promise: modiFiedpromise, cancel };
  }, [props.loginConfigId, props.toolName]);



  const getExtraFieldsData = useCallback((config) => {
    const { groupId, projectId, packageId, releaseId } = config;
    const projectName = projects.find((p) => p.id === projectId).name;
    const groupName = groups.find((g) => g.id === groupId).name;
    const packageName = packages.find((p) => p.id === packageId).name;
    const groupProjectAndPackageId = packages.find((p) => p.id === packageId)?.otherProperties?.groupProjectAndPackageId;
    const groupProjectAndPackageName = packages.find((p) => p.id === packageId)?.otherProperties?.groupProjectAndPackageName;
    const releaseNames = _.isEmpty(releases) ? ''
    : _(releases)
        .filter(r => _.includes(releaseId, r.id))
        .map('name')
        .value();
    return ({
      group: groupName,
      groupId: groupId,
      project: projectName,
      projectId: projectId,
      package: packageName,
      packageId: packageId,
      groupProjectAndPackageId: groupProjectAndPackageId,
      groupProjectAndPackageName: groupProjectAndPackageName,
      release: _.isEmpty(releaseNames) ? "" : releaseNames.join(),
      releaseId: _.isEmpty(releaseId) ? "" : releaseId.join(),
      ...(config.discovery_config_id && {
        discovery_config_id: config.discovery_config_id,
      }),
    });
  }, [groups, packages, projects, releases]);


  const onValueChange = useCallback((value, field, type) => {
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    const updatedConfiguration = [
      ...configurations,
    ];
    if(type === 'group'){
      updatedConfiguration[name] = {
        ...updatedConfiguration[name],
        projectId: undefined,
        packageId: undefined,
        releaseId: undefined
      }
    } else if(type === 'project'){
      updatedConfiguration[name] = {
        ...updatedConfiguration[name],
        packageId: undefined,
        releaseId: undefined
      }
    } else if(type === 'package'){
      updatedConfiguration[name] = {
        ...updatedConfiguration[name],
        releaseId: undefined
      }
    }
    
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const extraFields = useMemo(() => ([
    {
      name: "groupId",
      colSpan: 10,
      label: "Group",
      required: true,
      type: FieldTypes.SELECT,
      disabled: disableProjectFields,
      props: {
        api: fetchFieldResponse,
        autoFocus: !disableProjectFields,
        onChange: (value, field) => onValueChange(value, field, 'group')
      }
    },
    {
      name: "projectId",
      label: "Project",
      colSpan: 10,
      required: true,
      type: FieldTypes.SELECT,
      disabled: disableProjectFields,
      props: {
        setExtraProps: (fieldProps, currentField) => {
          const configurations = form.getFieldValue("configurations");
          const currentConfiguration = configurations[currentField.name] || {};
          const { groupId } = currentConfiguration;
          const keyName = groupId;

          if (groupId && !paramAPIRef.current[keyName]) {
            const groupName = groups.find((g) => g.id === groupId).name;
            paramAPIRef.current[keyName] = { project: JSON.stringify({ group: groupName }), type: 'project' };
          }
          return {
            api: groupId && fetchFieldResponse,
            apiParams: paramAPIRef.current[keyName],
            resetOptionsProp: paramAPIRef.current[keyName],
          }
        },
        onChange: (value, field) => onValueChange(value, field, 'project')
      }
    },
    {
      name: "packageId",
      label: "Package",
      colSpan: 10,
      required: true,
      type: FieldTypes.SELECT,
      disabled: disableProjectFields,
      props: {
        setExtraProps: (fieldProps, currentField) => {
          const configurations = form.getFieldValue("configurations");
          const currentConfiguration = configurations[currentField.name] || {};
          const { groupId, projectId } = currentConfiguration;
          const keyName = `${groupId}_${projectId}`
          if (groupId && projectId && !paramAPIRef.current[keyName]) {
            const projectName = projects.find((p) => p.id === projectId).name;
            const groupName = groups.find((g) => g.id === groupId).name;
            paramAPIRef.current[keyName] = { project: JSON.stringify({ group: groupName, groupId, project: projectName, projectId}), type: 'package' };
          }
          return {
            api: groupId && projectId && fetchFieldResponse,
            apiParams: paramAPIRef.current[keyName],
            resetOptionsProp: paramAPIRef.current[keyName],
          }
        },
        onChange: (value, field) => onValueChange(value, field, 'package')
      }
    },
    {
      name: "releaseId",
      label: "Release",
      colSpan: 10,
      type: FieldTypes.MULTI_SELECT,
      disabled: disableProjectFields,
      help: "You can select multiple releases. If none are selected, findings from the newest-created version / release will be included",
      props: {
        setExtraProps: (fieldProps, currentField) => {
          const configurations = form.getFieldValue("configurations");
          const currentConfiguration = configurations[currentField.name] || {};
          const { groupId, projectId, packageId } = currentConfiguration;
          const keyName = `${groupId}_${projectId}_${packageId}`;

          if (groupId && projectId && packageId && !paramAPIRef.current[keyName]) {
            const projectName = projects.find((p) => p.id === projectId).name;
            const groupName = groups.find((g) => g.id === groupId).name;
            const packageName = packages.find((p) => p.id === packageId).name;
            paramAPIRef.current[keyName] = { project: JSON.stringify({ group: groupName, groupId, project: projectName, projectId, package: packageName, packageId }), type: 'release' };
          }
          return {
            api: groupId && projectId && packageId && fetchFieldResponse,
            apiParams: paramAPIRef.current[keyName],
            resetOptionsProp: paramAPIRef.current[keyName],
          }
        },
      }
    }
  ]), [disableProjectFields, fetchFieldResponse, onValueChange, form, groups, projects, packages]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { releaseId } = config;
    return {
      releaseId: _.isEmpty(releaseId) ? [] : releaseId.split(','),
      ...(config.discovery_config_id && {
        discovery_config_id: config.discovery_config_id,
      }),
    };
  }, []);

  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      {...props}
    />
  )
})

export default ReversingLabSpectraMappingForm;
